import Sortable from "sortablejs"
import { Controller } from "@hotwired/stimulus"
import { ImageProcessor } from "./lib/image_cropper/image_processor"
import { TokenExpander } from "./lib/token_expander"

export default class extends Controller {
  static maxImageBlocks = 3
  static maxFileSizeBytes = 5 * 1024 * 1024
  static maxDimensionPx = 2500
  static pulseColors = {
    image: "#dbeafe",
    title: "#fef3c7",
    text: "#d1fae5",
  }

  static targets = [
    "blockList",
    "subjectInput",
    "supportEmailInput",
    "previewSubject",
    "previewBody",
    "previewTemplates",
    "editorBlockTemplates",
  ]
  static values = {
    nextIndex: Number,
    orgName: String,
    sampleName: String,
    sampleMembershipType: String,
    qrPreviewUrl: String,
    supportEmail: String,
  }

  connect() {
    this.imageProcessor = new ImageProcessor(null)
    this.previewObjectUrls = new Map()
    this.tokenExpander = new TokenExpander({
      "%org%": this.orgNameValue || "Your Organisation",
      "%name%": this.sampleNameValue || "Sarah Greene",
      "%membership_type%": this.sampleMembershipTypeValue || "Gold",
    })
    this.boundRealtimeRender = () => this.renderPreview()
    this.element.addEventListener("input", this.boundRealtimeRender)
    this.element.addEventListener("change", this.boundRealtimeRender)
    this.sortable = Sortable.create(this.blockListTarget, {
      handle: ".block-drag-handle",
      animation: 200,
      filter: ".block-locked",
      onSort: () => this.renderPreview(),
      onEnd: () => this.renumberPositions(),
    })
    this.renumberPositions()
    this.renderPreview()
  }

  disconnect() {
    this.revokeAllPreviewObjectUrls()
    this.element.removeEventListener("input", this.boundRealtimeRender)
    this.element.removeEventListener("change", this.boundRealtimeRender)
    if (this.sortable) this.sortable.destroy()
  }

  addBlock(event) {
    const type = event.currentTarget.dataset.blockType
    if (!type) return
    if (type === "image" && this.imageBlockCount() >= this.constructor.maxImageBlocks) {
      window.alert(`You can only add up to ${this.constructor.maxImageBlocks} image blocks.`)
      return
    }

    const idx = this.nextIndexValue
    this.nextIndexValue = idx + 1

    const html = this.editorBlockHtml(type, idx)
    if (!html) return

    const footer = this.blockListTarget.querySelector("[data-block-type='footer']")
    if (footer) {
      footer.insertAdjacentHTML("beforebegin", html)
    } else {
      this.blockListTarget.insertAdjacentHTML("beforeend", html)
    }
    const addedBlock = footer ? footer.previousElementSibling : this.blockListTarget.lastElementChild
    this.renumberPositions()
    this.applyNewBlockPulse(addedBlock, type)
    this.scrollToBlock(addedBlock)
  }

  removeBlock(event) {
    const block = event.currentTarget.closest("[data-block-entry]")
    if (block && !block.classList.contains("block-locked")) {
      const message = event.currentTarget.dataset.confirm || "Remove this block?"
      if (!window.confirm(message)) return

      this.revokeBlockPreviewObjectUrl(block)
      block.remove()
      this.renumberPositions()
    }
  }

  async handleImageFileChange(event) {
    const input = event.currentTarget
    const file = input.files?.[0]
    if (!file) return

    if (file.size > this.constructor.maxFileSizeBytes) {
      window.alert("Image must be under 5MB.")
      input.value = ""
      return
    }

    const processed = await this.resizeIfNeeded(file)
    if (!processed) {
      input.value = ""
      return
    }

    if (processed.size > this.constructor.maxFileSizeBytes) {
      window.alert("Image is still larger than 5MB after resizing. Please use a smaller source image.")
      input.value = ""
      return
    }

    this.setInputFile(input, processed)
    this.renderImagePreview(input, processed)
    this.renderPreview()
  }

  renumberPositions() {
    this.normalizeFooterPosition()

    const entries = this.blockListTarget.querySelectorAll("[data-block-entry]")
    entries.forEach((el, i) => {
      el.querySelectorAll("[data-position-field]").forEach((input) => {
        const name = input.getAttribute("name")
        if (name) {
          input.setAttribute("name", name.replace(/\[\d+\]/, `[${i}]`))
        }
      })
      const numberEl = el.querySelector("[data-block-number]")
      if (numberEl) {
        numberEl.textContent = i + 1
      }
    })
    this.renderPreview()
  }

  normalizeFooterPosition() {
    const footerBlocks = Array.from(this.blockListTarget.querySelectorAll("[data-block-type='footer']"))
    footerBlocks.forEach((footerBlock) => {
      this.blockListTarget.appendChild(footerBlock)
    })
  }

  imageBlockCount() {
    return this.blockListTarget.querySelectorAll(".block-entry--image").length
  }

  scrollToBlock(block) {
    if (!block) return

    block.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  async resizeIfNeeded(file) {
    const format = file.type === "image/jpeg" ? "jpeg" : "png"
    try {
      const resized = await this.imageProcessor.containWithinMax(
        file,
        this.constructor.maxDimensionPx,
        this.constructor.maxDimensionPx,
        format,
        0.9
      )
      return this.wrapBlobAsFile(resized, file)
    } catch {
      window.alert("Could not process image. Please try another file.")
      return null
    }
  }

  wrapBlobAsFile(blobOrFile, originalFile) {
    if (blobOrFile instanceof File) return blobOrFile

    return new File([blobOrFile], originalFile.name, {
      type: blobOrFile.type || originalFile.type,
      lastModified: Date.now(),
    })
  }

  setInputFile(input, file) {
    const dt = new DataTransfer()
    dt.items.add(file)
    input.files = dt.files
  }

  renderImagePreview(input, file) {
    const block = input.closest("[data-block-entry]")
    const preview = block?.querySelector("[data-image-preview]")
    if (!preview) return

    this.revokeBlockPreviewObjectUrl(block)
    const objectUrl = URL.createObjectURL(file)
    this.previewObjectUrls.set(block, objectUrl)
    preview.innerHTML = `<img src="${objectUrl}" alt="Image preview" class="block-image-preview">`
  }

  revokeBlockPreviewObjectUrl(block) {
    if (!block) return

    const objectUrl = this.previewObjectUrls.get(block)
    if (!objectUrl) return

    URL.revokeObjectURL(objectUrl)
    this.previewObjectUrls.delete(block)
  }

  revokeAllPreviewObjectUrls() {
    this.previewObjectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl))
    this.previewObjectUrls.clear()
  }

  renderPreview() {
    if (!this.hasPreviewSubjectTarget || !this.hasPreviewBodyTarget) return

    this.previewSubjectTarget.textContent = this.previewSubjectText()
    this.previewBodyTarget.replaceChildren(...this.previewBlocks())
  }

  previewSubjectText() {
    const raw = this.hasSubjectInputTarget ? this.subjectInputTarget.value : ""
    return this.expandTokens(raw)
  }

  previewBlocks() {
    const entries = this.blockListTarget.querySelectorAll("[data-block-entry]")
    return Array.from(entries).flatMap((entry) => {
      const block = this.previewBlock(entry)
      return block ? [block] : []
    })
  }

  previewBlock(entry) {
    const type = this.blockType(entry)
    const block = this.clonePreviewTemplate(type)
    if (!type || !block) return null

    if (type === "image") this.applyImagePreview(block, entry)
    if (type === "title") this.applyTextPreview(block, this.inputValue(entry, "input[name$='[text]']"))
    if (type === "text") this.applyTextPreview(block, this.inputValue(entry, "textarea[name$='[text]']"))
    if (type === "download") this.applyDownloadPreview(block)
    if (type === "footer") this.applyFooterPreview(block)
    return block
  }

  blockType(entry) {
    const typeInput = entry.querySelector("input[name$='[type]']")
    return typeInput?.value || null
  }

  clonePreviewTemplate(type) {
    if (!this.hasPreviewTemplatesTarget || !type) return null

    const template = this.previewTemplatesTarget.querySelector(`template[data-preview-template-type="${type}"]`)
    return template?.content.cloneNode(true) || null
  }

  applyTextPreview(block, text) {
    const textField = block.querySelector("[data-preview-field='text']")
    if (!textField) return

    textField.textContent = this.expandTokens(text)
  }

  applyImagePreview(block, entry) {
    const image = block.querySelector("[data-preview-field='image']")
    if (!image) return

    const placeholder = block.querySelector("[data-preview-field='image_placeholder']")
    const previewImage = entry.querySelector("[data-image-preview] img, .block-image-preview")
    const src = previewImage?.getAttribute("src") || ""
    const alt = this.inputValue(entry, "input[name$='[alt_text]']")

    image.setAttribute("alt", alt)

    if (src) {
      image.setAttribute("src", src)
      image.classList.remove("d-none")
      placeholder?.classList.add("d-none")
    } else {
      image.removeAttribute("src")
      image.classList.add("d-none")
      placeholder?.classList.remove("d-none")
    }
  }

  expandTokens(text) {
    return this.tokenExpander.expand(text)
  }

  applyDownloadPreview(block) {
    block.querySelectorAll("[data-preview-field='pass_url']").forEach((link) => {
      link.setAttribute("href", "#")
    })

    const qrCode = block.querySelector("[data-preview-field='qr_code']")
    if (qrCode) {
      qrCode.setAttribute("src", this.qrPreviewUrlValue || "")
    }
  }

  applyFooterPreview(block) {
    const link = block.querySelector("[data-preview-field='support_email']")
    if (!link) return

    const supportEmail = this.previewSupportEmail()
    link.textContent = ` ${supportEmail}`
    link.setAttribute("href", `mailto:${supportEmail}`)
  }

  previewSupportEmail() {
    if (!this.hasSupportEmailInputTarget) return this.supportEmailValue || ""
    return this.supportEmailInputTarget.value.toString()
  }

  inputValue(entry, selector) {
    const el = entry.querySelector(selector)
    return el ? el.value.toString() : ""
  }

  applyNewBlockPulse(block, type) {
    if (!block) return

    const pulseColor = this.constructor.pulseColors[type]
    if (pulseColor) {
      block.style.setProperty("--new-block-pulse-color", pulseColor)
    }
    block.classList.add("block-entry--newly-added")

    window.setTimeout(() => {
      block.classList.remove("block-entry--newly-added")
      block.style.removeProperty("--new-block-pulse-color")
    }, 5000)
  }

  editorBlockHtml(type, idx) {
    if (!this.hasEditorBlockTemplatesTarget) return ""

    const templateEl = this.editorBlockTemplatesTarget.querySelector(`template[data-editor-block-type="${type}"]`)
    if (!templateEl) return ""

    return this.fillEditorTemplate(templateEl.innerHTML, type, idx)
  }

  fillEditorTemplate(html, type, idx) {
    let result = html.replaceAll("__BLOCK_INDEX__", String(idx))
    result = result.replaceAll("__BLOCK_NUMBER__", String(idx + 1))
    if (type === "image") {
      result = result.replaceAll("__IMAGE_KEY__", this.generateImageKey())
    }
    return result
  }

  generateImageKey() {
    return `image_${globalThis.crypto.randomUUID()}`
  }
}
